import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { Card, CardHeader, CardHeaderToolbar } from "../../../../_metronic/_partials/controls";
import IncomingCallsTable from "./incoming_calls_detail/incomingCallsTable";
import { incomingCallsAction, getAllIncomingCallsAsync, importIncomingCallsAsync, downloadSampleExcelAsync } from "../redux";
import Select from "react-select";
import { useIsAccessAllow } from "../../users/redux";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../../_metronic/_helpers";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./incomingCalls.scss";

export const IncomingCalls = () => {
  const dispatch = useDispatch();
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const fromDateContainerRef = useRef(null);
  const toDateContainerRef = useRef(null);
  const [isImporting, setIsImporting] = useState(false);

  const { refreshIncomingCallsList, searchBy, searchText, audioType, fromDateFilter, toDateFilter, leadType, callOutcomeFilter } = useSelector(
    (state) => state.incomingCalls,
    shallowEqual
  );

  useEffect(() => {
    if (refreshIncomingCallsList) {
      dispatch(getAllIncomingCallsAsync(searchBy, searchText, audioType, fromDateFilter, toDateFilter, leadType, callOutcomeFilter));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshIncomingCallsList, searchBy, searchText, audioType, fromDateFilter, toDateFilter, leadType, callOutcomeFilter]);

  // Reset refresh flag on component mount to ensure API call happens
  useEffect(() => {
    dispatch(incomingCallsAction.resetRefreshFlag());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fromDateInput = fromDateContainerRef.current?.querySelector('input');
    if (fromDateInput) {
      const handleFocus = () => {
        fromDateContainerRef.current?.classList.add('date-picker-focused');
      };
      const handleBlur = () => {
        fromDateContainerRef.current?.classList.remove('date-picker-focused');
      };
      fromDateInput.addEventListener('focus', handleFocus);
      fromDateInput.addEventListener('blur', handleBlur);
      return () => {
        fromDateInput.removeEventListener('focus', handleFocus);
        fromDateInput.removeEventListener('blur', handleBlur);
      };
    }
  }, [fromDateFilter]);

  useEffect(() => {
    const toDateInput = toDateContainerRef.current?.querySelector('input');
    if (toDateInput) {
      const handleFocus = () => {
        toDateContainerRef.current?.classList.add('date-picker-focused');
      };
      const handleBlur = () => {
        toDateContainerRef.current?.classList.remove('date-picker-focused');
      };
      toDateInput.addEventListener('focus', handleFocus);
      toDateInput.addEventListener('blur', handleBlur);
      return () => {
        toDateInput.removeEventListener('focus', handleFocus);
        toDateInput.removeEventListener('blur', handleBlur);
      };
    }
  }, [toDateFilter]);

  const onSearchTextChange = (event) => {
    dispatch(incomingCallsAction.searchTextChange(event.target.value));
  };

  const audioTypeOptions = [
    { value: "all", label: "All Data" },
    { value: "with_audio", label: "With Audio" },
    { value: "with_out_audio", label: "Without Audio" },
  ];

  const defaultAudioType =
    audioTypeOptions.find((option) => option.value === audioType) || audioTypeOptions[0];

  const changeAudioType = (option) => {
    dispatch(incomingCallsAction.setAudioType(option.value));
  };

  const leadTypeOptions = [
    { value: "all", label: "All Leads" },
    { value: "Blocked", label: "Blocked" },
    { value: "Unblock", label: "Unblock" },
  ];

  const defaultLeadType = leadTypeOptions.find((option) => option.value === leadType) || leadTypeOptions[0];

  const changeLeadType = (option) => {
    dispatch(incomingCallsAction.setLeadType(option.value));
  };

  const callOutcomeOptions = [
    { value: "all", label: "All Outcomes" },
    { value: "Call Concluded", label: "Call Concluded" },
    { value: "Not Concluded", label: "Not Concluded" },
    { value: "Not Interested", label: "Not Interested" },
    { value: "Interested in future", label: "Interested in future" },
    { value: "No Call", label: "No Call" },
  ];

  const defaultCallOutcome = callOutcomeOptions.find((option) => option.value === callOutcomeFilter) || callOutcomeOptions[0];

  const changeCallOutcomeFilter = (option) => {
    dispatch(incomingCallsAction.setCallOutcomeFilter(option.value));
  };

  // Format date to DD/MM/YYYY for API
  const formatDateForAPI = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Parse date from DD/MM/YYYY string
  const parseDateFromAPI = (dateStr) => {
    if (!dateStr) return null;
    // Handle DD/MM/YYYY format
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed in Date
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    // Fallback to default Date parsing for other formats
    return new Date(dateStr);
  };

  const handleFromDateChange = (date) => {
    const formattedDate = date ? formatDateForAPI(date) : "";
    dispatch(incomingCallsAction.setFromDateFilter(formattedDate));
    // If new From Date is after the currently selected To Date, clear To Date
    if (date && toDateFilter) {
      const currentToDate = parseDateFromAPI(toDateFilter);
      if (currentToDate && date > currentToDate) {
        dispatch(incomingCallsAction.setToDateFilter(""));
      }
    }
  };

  const handleToDateChange = (date) => {
    // Prevent selecting a To Date that is before the From Date
    if (date && fromDateFilter) {
      const currentFromDate = parseDateFromAPI(fromDateFilter);
      if (currentFromDate && date < currentFromDate) {
        return; // Ignore the invalid selection
      }
    }
    const formattedDate = date ? formatDateForAPI(date) : "";
    dispatch(incomingCallsAction.setToDateFilter(formattedDate));
  };

  const handleDownloadSample = async () => {
    await dispatch(downloadSampleExcelAsync());
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
    ];
    const isValidType = validTypes.includes(file.type) || file.name.match(/\.(xlsx|xls)$/i);

    if (!isValidType) {
      // Show error message for invalid file type
      dispatch(showSuccessSnackbar("error", "Unsupported file type. Please upload an Excel file.", 5000));
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    setIsImporting(true);
    try {
      const result = await dispatch(importIncomingCallsAsync(file));
      if (result?.success) {
        // Refresh the list after successful import
        dispatch(getAllIncomingCallsAsync(searchBy, searchText, audioType, fromDateFilter, toDateFilter, leadType, callOutcomeFilter));
      }
    } catch (error) {
      console.error("Import error:", error);
    } finally {
      setIsImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const isSearchAllow = useIsAccessAllow("incomingCalls.list");

  return (
    <Card style={{ background: "#ffffff", borderRadius: 16, boxShadow: "0 15px 40px rgba(15, 23, 42, 0.08)", border: "1px solid #e5e7eb", overflow: "hidden" }}>
      <CardHeader
        title={
          <span style={{ fontSize: "20px", fontWeight: 600 }}>Incoming Calls</span>
        }
      >
        <CardHeaderToolbar>
          <div className="d-flex flex-wrap ap_filter_wraper justify-content-end align-items-center margin-left-auto" style={{ position: 'relative', zIndex: 10 }}>
            <form className="subheader_filter" onSubmit={(event) => event.preventDefault()}>
              <div className="form-row">
                <div className="form-group mr-3 mb-1 p-0" style={{ width: "200px", marginRight: "50px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="Select audio type"
                    className="custom-react-select"
                    options={audioTypeOptions}
                    onChange={changeAudioType}
                    value={defaultAudioType}
                  />
                </div>
                {isSearchAllow && (
                  <div className="form-group mr-3 mb-1">
                    <div className="custom-search-input">
                      <input
                        name="Search"
                        placeholder="Search..."
                        type="text"
                        value={searchText}
                        autoComplete="off"
                        onChange={onSearchTextChange}
                        ref={searchInputRef}
                      />
                      <div className="search-icon-wrapper">
                        <span className="svg-icon">
                          <SVG src={toAbsoluteUrl("/media/svg/icons/custom/search-outline.svg")} />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {isSearchAllow && (
                  <>
                    <div className="form-group mr-3 mb-1" style={{ width: "160px" }}>
                      <div
                        className="custom-date-picker-input"
                        ref={fromDateContainerRef}
                      >
                        <DatePicker
                          selected={parseDateFromAPI(fromDateFilter)}
                          onChange={handleFromDateChange}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="From Date"
                          maxDate={parseDateFromAPI(toDateFilter) || new Date()}
                          isClearable
                          wrapperClassName="date-picker-wrapper"
                        />
                        {!fromDateFilter && (
                          <div className="calendar-icon-wrapper">
                            <span className="svg-icon">
                              <SVG src={toAbsoluteUrl("/media/svg/icons/custom/calendar.svg")} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-group mr-3 mb-1" style={{ width: "160px" }}>
                      <div
                        className="custom-date-picker-input"
                        ref={toDateContainerRef}
                      >
                        <DatePicker
                          selected={parseDateFromAPI(toDateFilter)}
                          onChange={handleToDateChange}
                          dateFormat="dd/MM/yyyy"
                          placeholderText="To Date"
                          minDate={parseDateFromAPI(fromDateFilter)}
                          maxDate={new Date()}
                          isClearable
                          wrapperClassName="date-picker-wrapper"
                        />
                        {!toDateFilter && (
                          <div className="calendar-icon-wrapper">
                            <span className="svg-icon">
                              <SVG src={toAbsoluteUrl("/media/svg/icons/custom/calendar.svg")} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}



                {/* <div className="form-group mr-3 mb-1 p-0" style={{ width: "150px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Leads"
                    className="custom-react-select"
                    options={leadTypeOptions}
                    onChange={changeLeadType}
                    value={defaultLeadType}
                  />
                </div> */}

                <div className="form-group mr-3 mb-1 p-0" style={{ width: "180px" }}>
                  <Select
                    classNamePrefix="react-select"
                    placeholder="All Outcomes"
                    className="custom-react-select"
                    options={callOutcomeOptions}
                    onChange={changeCallOutcomeFilter}
                    value={defaultCallOutcome}
                  />
                </div>
              </div>
            </form>
          </div>
        </CardHeaderToolbar>
      </CardHeader>
      <IncomingCallsTable />
    </Card>
  );
};

export default IncomingCalls;

